/**
 * Location sync middleware
 *
 * Keeps Redux store in sync with browser location Captures both browser navigation and programmatic navigation
 */
import { createListenerMiddleware } from '@reduxjs/toolkit'
import { setLocation } from '~/store/location'

const logger = console.scope('locationSync')

// Create the middleware
const locationSync = createListenerMiddleware()

// Store reference for browser event handlers
let storeRef = null

/**
 * Update location in Redux store
 */
const updateLocationInStore = () => {
	if (!storeRef) return

	storeRef.dispatch(
		setLocation({
			pathname: window.location.pathname,
			search: window.location.search,
			hash: window.location.hash,
			href: window.location.href,
		})
	)
}

/**
 * Initialize location sync with browser events Should be called once when store is created
 */
export const initLocationSync = store => {
	storeRef = store

	// Update location on initial load
	updateLocationInStore()

	// Listen to browser back/forward navigation
	window.addEventListener('popstate', updateLocationInStore)

	// Patch pushState to capture programmatic navigation
	const originalPushState = window.history.pushState
	window.history.pushState = (...args) => {
		originalPushState.apply(window.history, args)
		updateLocationInStore()
	}

	// Patch replaceState as well
	const originalReplaceState = window.history.replaceState
	window.history.replaceState = (...args) => {
		originalReplaceState.apply(window.history, args)
		updateLocationInStore()
	}

	// Handle hash changes
	window.addEventListener('hashchange', updateLocationInStore)
}

// Listen for navigation actions from React Router or custom navigation
locationSync.startListening({
	predicate: action => {
		// Listen for React Router navigation actions if they exist
		return (
			action.type === '@@router/LOCATION_CHANGE' ||
			action.type === 'NAVIGATE' ||
			action.type === 'navigation/navigate'
		)
	},
	effect: async (action, { dispatch }) => {
		// Update location based on navigation action
		if (action.payload?.location) {
			dispatch(setLocation(action.payload.location))
		}
	},
})

// Listen for manual location updates to sync with browser
locationSync.startListening({
	actionCreator: setLocation,
	effect: async action => {
		const { pathname, search, hash } = action.payload
		const currentPath = window.location.pathname + window.location.search + window.location.hash
		const newPath = pathname + search + hash

		// Only update browser if location actually changed
		if (currentPath !== newPath) {
			// Use replaceState to avoid adding to history
			window.history.replaceState(null, '', newPath)
			logger.debug('Browser location updated:', newPath)
		}
	},
})

export default locationSync
